import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Users, Building2, BarChart2, LogOut, ChevronLeft, Search, AlertTriangle, CheckCircle, SlidersHorizontal, Download, ClipboardList } from 'lucide-react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';

// Storage RLS で保護された検査画像を表示するコンポーネント
// DBに保存されているのはストレージパス。Supabaseクライアント（セッション付き）で
// ダウンロードし、Blob URL として表示する。期限切れの心配がない。
function StorageImage({ path, className, alt }) {
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    if (!path) return;
    // 旧データ（フルURL）との後方互換性
    if (path.startsWith('http')) {
      setObjectUrl(path);
      return;
    }
    let cancelled = false;
    let created = null;
    supabase.storage
      .from('inspection-images')
      .download(path)
      .then(({ data }) => {
        if (data && !cancelled) {
          created = URL.createObjectURL(data);
          setObjectUrl(created);
        }
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [path]);
  if (!objectUrl) return null;
  return (
    <a href={objectUrl} target="_blank" rel="noreferrer">
      <img src={objectUrl} className={className} alt={alt} />
    </a>
  );
}

// CSV ダウンロードユーティリティ（UTF-8 BOM付き → Excelで文字化けしない）
function exportCSV(headers, rows, filename) {
  const BOM = '\uFEFF';
  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = BOM + [headers, ...rows].map(row => row.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// =============================================
// 企業Admin画面（自社のユーザー・店舗・部門・使用量）
// =============================================
function CompanyAdminPanel({ onClose }) {
  const { profile, company, signOut } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(false);

  // ユーザー招待
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  // 店舗追加
  const [newStoreName, setNewStoreName] = useState('');
  // 部門追加
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptStoreId, setNewDeptStoreId] = useState('');

  // 検査履歴
  const [inspHistory, setInspHistory] = useState([]);
  const [inspDateFrom, setInspDateFrom] = useState('');
  const [inspDateTo, setInspDateTo] = useState('');
  const [inspStatus, setInspStatus] = useState('');
  const [inspLoading, setInspLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('company_id', company.id)
      .order('created_at');
    setUsers(data || []);
  }, [company]);

  const fetchStores = useCallback(async () => {
    const { data } = await supabase
      .from('stores')
      .select('*')
      .eq('company_id', company.id)
      .order('name');
    setStores(data || []);
  }, [company]);

  const fetchDepartments = useCallback(async () => {
    const { data } = await supabase
      .from('departments')
      .select('*, store:stores(name)')
      .eq('company_id', company.id)
      .order('name');
    setDepartments(data || []);
  }, [company]);

  const fetchUsage = useCallback(async () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const { data } = await supabase
      .from('usage_daily')
      .select('date, match_count')
      .eq('company_id', company.id)
      .gte('date', firstDay)
      .order('date');
    setUsage(data || []);
  }, [company]);

  useEffect(() => {
    if (!company) return;
    setLoading(true);
    Promise.all([fetchUsers(), fetchStores(), fetchDepartments(), fetchUsage()])
      .finally(() => setLoading(false));
  }, [company, fetchUsers, fetchStores, fetchDepartments, fetchUsage]);

  const inviteUser = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const res = await fetch('/api/invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentSession?.access_token}`,
      },
      body: JSON.stringify({
        email: inviteEmail.trim(),
        companyId: company.id,
        role: 'member',
        name: inviteName.trim() || inviteEmail.trim()
      })
    });
    const data = await res.json();
    setInviting(false);
    if (data.error) {
      alert('招待エラー: ' + data.error);
    } else {
      setInviteEmail('');
      setInviteName('');
      alert('招待メールを送信しました');
      fetchUsers();
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('このユーザーを削除しますか？')) return;
    await supabase.from('user_profiles').update({ is_active: false }).eq('id', userId);
    fetchUsers();
  };

  const updateUserRole = async (userId, newRole) => {
    if (newRole === 'admin' && company?.max_admin > 0) {
      const adminCount = users.filter(u => u.is_active && u.role === 'admin').length;
      if (adminCount >= company.max_admin) {
        alert(`管理者の上限（${company.max_admin}名）に達しています。`);
        return;
      }
    }
    const { error } = await supabase.from('user_profiles').update({ role: newRole }).eq('id', userId);
    if (error) { alert('ロール変更エラー: ' + error.message); return; }
    fetchUsers();
  };

  const addStore = async () => {
    if (!newStoreName.trim()) return;
    await supabase.from('stores').insert({ company_id: company.id, name: newStoreName.trim() });
    setNewStoreName('');
    fetchStores();
  };

  const deleteStore = async (storeId) => {
    if (!window.confirm('この店舗を削除しますか？（紐づく部門も削除されます）')) return;
    await supabase.from('stores').delete().eq('id', storeId);
    fetchStores();
    fetchDepartments();
  };

  const addDepartment = async () => {
    if (!newDeptName.trim() || !newDeptStoreId) return;
    await supabase.from('departments').insert({
      company_id: company.id,
      store_id: newDeptStoreId,
      name: newDeptName.trim()
    });
    setNewDeptName('');
    fetchDepartments();
  };

  const deleteDepartment = async (deptId) => {
    await supabase.from('departments').delete().eq('id', deptId);
    fetchDepartments();
  };

  const fetchInspHistory = useCallback(async () => {
    setInspLoading(true);
    let query = supabase
      .from('inspections')
      .select('serial, store_name, dept_name, lot_number, label_product_name, detected_food_visual, match_status, reason, inspected_at, user_email')
      .eq('company_id', company.id)
      .order('inspected_at', { ascending: false })
      .limit(500);
    if (inspDateFrom) query = query.gte('inspected_at', inspDateFrom + 'T00:00:00+09:00');
    if (inspDateTo)   query = query.lte('inspected_at', inspDateTo   + 'T23:59:59+09:00');
    if (inspStatus)   query = query.eq('match_status', inspStatus);
    const { data } = await query;
    setInspHistory(data || []);
    setInspLoading(false);
  }, [company, inspDateFrom, inspDateTo, inspStatus]);

  const exportInspHistoryCSV = () => {
    const headers = ['シリアル', '店舗', '部門', 'ロット番号', '商品名(ラベル)', '目視識別', '判定', '判定理由', '検査日時', '担当者メール'];
    const rows = inspHistory.map(r => [
      r.serial ?? '', r.store_name ?? '', r.dept_name ?? '', r.lot_number ?? '',
      r.label_product_name ?? '', r.detected_food_visual ?? '', r.match_status ?? '',
      r.reason ?? '',
      r.inspected_at ? new Date(r.inspected_at).toLocaleString('ja-JP') : '',
      r.user_email ?? ''
    ]);
    const date = new Date().toISOString().slice(0, 10);
    exportCSV(headers, rows, `検査履歴_${company.name}_${date}.csv`);
  };

  const monthlyTotal = usage.reduce((sum, r) => sum + r.match_count, 0);

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* ヘッダー */}
      <header className="bg-indigo-700 text-white px-4 py-3 flex items-center gap-3">
        <button onClick={onClose} className="text-indigo-300 hover:text-white">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="font-bold">{company?.name}</div>
          <div className="text-xs text-indigo-300">{profile?.name}（{profile?.role === 'admin' ? '管理者' : 'メンバー'}）</div>
        </div>
        <button onClick={signOut} className="text-xs text-indigo-300 hover:text-white flex items-center gap-1">
          <LogOut className="w-4 h-4" /> ログアウト
        </button>
      </header>

      {/* タブ */}
      <div className="flex border-b bg-white">
        {[
          { key: 'users', label: 'ユーザー', icon: Users },
          { key: 'stores', label: '店舗・部門', icon: Building2 },
          { key: 'usage', label: '使用量', icon: BarChart2 },
          { key: 'inspections', label: '検査履歴', icon: ClipboardList },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
              tab === key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 max-w-lg mx-auto w-full">
        {loading ? (
          <div className="text-center py-10 text-slate-400">読み込み中...</div>
        ) : (
          <>
            {/* ユーザー管理 */}
            {tab === 'users' && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-slate-500">ユーザーを招待</p>
                  <input
                    type="email"
                    placeholder="メールアドレス"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <input
                    type="text"
                    placeholder="表示名（任意）"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <button
                    onClick={inviteUser}
                    disabled={inviting || !inviteEmail.trim()}
                    className="w-full py-2 bg-indigo-600 text-white text-sm font-bold rounded disabled:bg-slate-300"
                  >
                    {inviting ? '送信中...' : '招待メールを送る'}
                  </button>
                </div>

                <div className="space-y-1">
                  {users.map(u => (
                    <div key={u.id} className={`flex items-center justify-between p-2 rounded border ${u.is_active ? 'bg-white' : 'bg-slate-50 opacity-50'}`}>
                      <div>
                        <div className="text-sm font-medium">{u.name || '（名前未設定）'}</div>
                        <div className="text-xs text-slate-400">
                          {u.role === 'admin' ? '管理者' : u.role === 'superadmin' ? 'SuperAdmin' : 'メンバー'}
                          {!u.is_active && ' · 無効'}
                        </div>
                      </div>
                      {u.id !== profile?.id && u.role !== 'superadmin' && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateUserRole(u.id, u.role === 'admin' ? 'member' : 'admin')}
                            className={`text-xs px-2 py-1 rounded border ${u.role === 'admin' ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                          >
                            {u.role === 'admin' ? '管理者' : 'メンバー'}
                          </button>
                          <button onClick={() => deleteUser(u.id)} className="text-slate-300 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 店舗・部門管理 */}
            {tab === 'stores' && (
              <div className="space-y-4">
                {/* 店舗追加 */}
                <div>
                  <p className="text-xs font-bold text-slate-500 mb-1">店舗を追加</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="店舗名"
                      value={newStoreName}
                      onChange={(e) => setNewStoreName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addStore()}
                      className="flex-1 p-2 border rounded text-sm"
                    />
                    <button onClick={addStore} className="px-3 py-2 bg-indigo-600 text-white rounded text-sm">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* 店舗一覧 */}
                <div className="space-y-2">
                  {stores.map(store => (
                    <div key={store.id} className="bg-white border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{store.name}</span>
                        <button onClick={() => deleteStore(store.id)} className="text-slate-300 hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {/* この店舗の部門 */}
                      <div className="space-y-1 pl-2 border-l-2 border-slate-100">
                        {departments.filter(d => d.store_id === store.id).map(dept => (
                          <div key={dept.id} className="flex items-center justify-between text-xs text-slate-600">
                            <span>{dept.name}</span>
                            <button onClick={() => deleteDepartment(dept.id)} className="text-slate-300 hover:text-red-500 ml-2">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* 部門追加 */}
                {stores.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-slate-500 mb-1">部門を追加</p>
                    <div className="flex gap-2">
                      <select
                        value={newDeptStoreId}
                        onChange={(e) => setNewDeptStoreId(e.target.value)}
                        className="p-2 border rounded text-sm"
                      >
                        <option value="">店舗を選択</option>
                        {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <input
                        type="text"
                        placeholder="部門名"
                        value={newDeptName}
                        onChange={(e) => setNewDeptName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
                        className="flex-1 p-2 border rounded text-sm"
                      />
                      <button onClick={addDepartment} className="px-3 py-2 bg-indigo-600 text-white rounded text-sm">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 使用量 */}
            {tab === 'usage' && (
              <div className="space-y-4">
                <div className="bg-indigo-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-indigo-700">{monthlyTotal.toLocaleString()}</div>
                  <div className="text-sm text-indigo-500 mt-1">今月の照合回数</div>
                </div>

                <div>
                  <p className="text-xs font-bold text-slate-500 mb-2">日別内訳（今月）</p>
                  {usage.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">データなし</p>
                  ) : (
                    <div className="space-y-1">
                      {[...usage].reverse().map(row => (
                        <div key={row.date} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                          <span className="text-slate-600 font-mono text-xs">{row.date}</span>
                          <span className="font-bold text-indigo-700">{row.match_count.toLocaleString()} 回</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'inspections' && (
              <div className="space-y-3">
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-slate-500">検索条件（最大500件）</p>
                  <div className="flex gap-2 items-center">
                    <input type="date" value={inspDateFrom} onChange={e => setInspDateFrom(e.target.value)} className="flex-1 p-2 border rounded text-sm" />
                    <span className="text-slate-400 text-xs flex-shrink-0">〜</span>
                    <input type="date" value={inspDateTo} onChange={e => setInspDateTo(e.target.value)} className="flex-1 p-2 border rounded text-sm" />
                  </div>
                  <select value={inspStatus} onChange={e => setInspStatus(e.target.value)} className="w-full p-2 border rounded text-sm">
                    <option value="">判定（全件）</option>
                    <option value="SAFE">SAFE</option>
                    <option value="DANGER">DANGER</option>
                    <option value="ERROR">ERROR</option>
                  </select>
                  <button
                    onClick={fetchInspHistory}
                    disabled={inspLoading}
                    className="w-full py-2 bg-indigo-600 text-white text-sm font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    {inspLoading ? '検索中...' : '検索'}
                  </button>
                </div>

                {inspHistory.length > 0 && (
                  <button
                    onClick={exportInspHistoryCSV}
                    className="w-full py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-bold rounded flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" /> CSV出力 ({inspHistory.length}件)
                  </button>
                )}

                {inspHistory.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {inspLoading ? '' : '条件を指定して検索してください'}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {inspHistory.map((insp, i) => (
                      <div key={insp.serial ?? i} className="p-2 bg-white rounded border">
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-2">
                            {insp.serial != null && (
                              <span className="font-mono text-xs font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">#{insp.serial}</span>
                            )}
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                              insp.match_status === 'DANGER' ? 'bg-red-100 text-red-700' :
                              insp.match_status === 'ERROR'  ? 'bg-slate-100 text-slate-500' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {insp.match_status === 'DANGER' ? <AlertTriangle className="w-3 h-3 inline mr-0.5" /> : insp.match_status === 'SAFE' ? <CheckCircle className="w-3 h-3 inline mr-0.5" /> : null}
                              {insp.match_status}
                            </span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(insp.inspected_at).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500">{insp.store_name || '—'} · {insp.dept_name || '—'}</div>
                        <div className="text-xs text-slate-600 truncate">{insp.label_product_name || '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================
// SuperAdmin画面（全企業管理）
// =============================================
function SuperAdminPanel({ onClose }) {
  const { signOut } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [usageSummary, setUsageSummary] = useState([]);
  const [emailRegs, setEmailRegs] = useState([]);
  const [tab, setTab] = useState('companies');
  const [loading, setLoading] = useState(true);

  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyPlan, setNewCompanyPlan] = useState('trial');
  const [adminEmail, setAdminEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingLimits, setEditingLimits] = useState({});

  // 検査履歴検索
  const [inspections, setInspections] = useState([]);
  const [searchSerial, setSearchSerial] = useState('');
  const [searchCompanyId, setSearchCompanyId] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchDateFrom, setSearchDateFrom] = useState('');
  const [searchDateTo, setSearchDateTo] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  // システム設定
  const [settings, setSettings] = useState({ magic_link_required: false, invite_enabled: true });
  const [savingKey, setSavingKey] = useState(null);

  const fetchCompanies = useCallback(async () => {
    const { data } = await supabase.from('companies').select('*').order('created_at', { ascending: false });
    setCompanies(data || []);
  }, []);

  const fetchUsageSummary = useCallback(async () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const { data } = await supabase
      .from('usage_daily')
      .select('company_id, match_count, companies(name)')
      .gte('date', firstDay);

    if (data) {
      const map = {};
      data.forEach(row => {
        if (!map[row.company_id]) map[row.company_id] = { name: row.companies?.name, total: 0 };
        map[row.company_id].total += row.match_count;
      });
      setUsageSummary(Object.entries(map).map(([id, v]) => ({ id, ...v })).sort((a, b) => b.total - a.total));
    }
  }, []);

  const fetchEmailRegs = useCallback(async () => {
    const { data } = await supabase
      .from('email_registrations')
      .select('*')
      .order('last_seen', { ascending: false });
    setEmailRegs(data || []);
  }, []);

  const fetchInspections = useCallback(async () => {
    setSearchLoading(true);
    let query = supabase
      .from('inspections')
      .select('serial, id, company_id, user_email, store_name, dept_name, lot_number, label_product_name, detected_food_visual, match_status, reason, image_urls, inspected_at')
      .order('inspected_at', { ascending: false })
      .limit(100);
    if (searchSerial.trim()) query = query.eq('serial', parseInt(searchSerial));
    if (searchCompanyId) query = query.eq('company_id', searchCompanyId);
    if (searchStatus) query = query.eq('match_status', searchStatus);
    if (searchDateFrom) query = query.gte('inspected_at', searchDateFrom + 'T00:00:00+09:00');
    if (searchDateTo) query = query.lte('inspected_at', searchDateTo + 'T23:59:59+09:00');
    const { data } = await query;
    setInspections(data || []);
    setSearchLoading(false);
  }, [searchSerial, searchCompanyId, searchStatus, searchDateFrom, searchDateTo]);

  const fetchSettings = useCallback(async () => {
    const { data } = await supabase.from('system_settings').select('key, value');
    if (data) {
      const map = {};
      data.forEach(r => { map[r.key] = r.value; });
      setSettings(prev => ({ ...prev, ...map }));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCompanies(), fetchUsageSummary(), fetchEmailRegs(), fetchSettings()])
      .finally(() => setLoading(false));
  }, [fetchCompanies, fetchUsageSummary, fetchEmailRegs, fetchSettings]);

  const updateSetting = async (key, value) => {
    setSavingKey(key);
    await supabase.from('system_settings').upsert({ key, value, updated_at: new Date().toISOString() });
    setSettings(prev => ({ ...prev, [key]: value }));
    setSavingKey(null);
  };

  const createCompany = async () => {
    if (!newCompanyName.trim() || !adminEmail.trim()) return;
    setCreating(true);
    const { data: company, error: cErr } = await supabase
      .from('companies')
      .insert({ name: newCompanyName.trim(), plan: newCompanyPlan })
      .select()
      .single();

    if (cErr) { alert('企業作成エラー: ' + cErr.message); setCreating(false); return; }

    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const iRes = await fetch('/api/invite', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentSession?.access_token}`,
      },
      body: JSON.stringify({
        email: adminEmail.trim(),
        companyId: company.id,
        role: 'admin',
        name: newCompanyName.trim() + ' 管理者'
      })
    });
    const iData = await iRes.json();
    setCreating(false);
    if (iData.error) {
      alert('招待エラー: ' + iData.error);
    } else {
      setNewCompanyName('');
      setAdminEmail('');
      alert('企業を作成し、管理者へ招待メールを送信しました');
      fetchCompanies();
    }
  };

  const exportInspectionsCSV = () => {
    const headers = ['シリアル', '企業', '店舗', '部門', 'ロット番号', '商品名(ラベル)', '目視識別', '判定', '判定理由', '検査日時', '担当者メール'];
    const rows = inspections.map(r => [
      r.serial ?? '',
      companies.find(c => c.id === r.company_id)?.name ?? '',
      r.store_name ?? '', r.dept_name ?? '', r.lot_number ?? '',
      r.label_product_name ?? '', r.detected_food_visual ?? '',
      r.match_status ?? '', r.reason ?? '',
      r.inspected_at ? new Date(r.inspected_at).toLocaleString('ja-JP') : '',
      r.user_email ?? ''
    ]);
    const date = new Date().toISOString().slice(0, 10);
    exportCSV(headers, rows, `検査履歴_全社_${date}.csv`);
  };

  const toggleActive = async (company) => {
    await supabase.from('companies').update({ is_active: !company.is_active }).eq('id', company.id);
    fetchCompanies();
  };

  const toggleAllowOverage = async (company) => {
    await supabase.from('companies').update({ allow_overage: !company.allow_overage }).eq('id', company.id);
    fetchCompanies();
  };

  const updateCompanyLimit = async (companyId, field, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    await supabase.from('companies').update({ [field]: num }).eq('id', companyId);
    setEditingLimits(prev => { const n = { ...prev }; delete n[`${companyId}_${field}`]; return n; });
    fetchCompanies();
  };

  // トグルスイッチUI
  const Toggle = ({ value, onChange, disabled }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${value ? 'bg-slate-700' : 'bg-slate-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <header className="bg-slate-800 text-white px-4 py-3 flex items-center gap-3">
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 font-bold">SuperAdmin</div>
        <button onClick={signOut} className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
          <LogOut className="w-4 h-4" /> ログアウト
        </button>
      </header>

      <div className="flex border-b bg-white overflow-x-auto">
        {[
          { key: 'companies', label: '契約企業', icon: Building2 },
          { key: 'usage', label: '使用量', icon: BarChart2 },
          { key: 'emails', label: '登録メール', icon: Users },
          { key: 'inspections', label: '検査履歴', icon: Search },
          { key: 'settings', label: '設定', icon: SlidersHorizontal },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-shrink-0 flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
              tab === key ? 'border-slate-700 text-slate-800' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 max-w-lg mx-auto w-full">
        {loading ? (
          <div className="text-center py-10 text-slate-400">読み込み中...</div>
        ) : (
          <>
            {tab === 'companies' && (
              <div className="space-y-4">
                {/* 企業新規作成（invite_enabled がONの時のみ表示） */}
                {settings.invite_enabled ? (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-bold text-slate-500">新規企業を追加</p>
                    <input
                      type="text"
                      placeholder="企業名"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      className="w-full p-2 border rounded text-sm"
                    />
                    <select
                      value={newCompanyPlan}
                      onChange={(e) => setNewCompanyPlan(e.target.value)}
                      className="w-full p-2 border rounded text-sm"
                    >
                      <option value="trial">Trial</option>
                      <option value="standard">Standard</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                    <input
                      type="email"
                      placeholder="管理者メールアドレス"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      className="w-full p-2 border rounded text-sm"
                    />
                    <button
                      onClick={createCompany}
                      disabled={creating || !newCompanyName.trim() || !adminEmail.trim()}
                      className="w-full py-2 bg-slate-700 text-white text-sm font-bold rounded disabled:bg-slate-300"
                    >
                      {creating ? '作成中...' : '企業を作成して招待'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-3 text-center text-xs text-slate-400">
                    企業作成・招待機能は無効です（設定タブから有効化できます）
                  </div>
                )}

                {/* 企業一覧 */}
                <div className="space-y-2">
                  {companies.map(c => (
                    <div key={c.id} className={`p-3 rounded-lg border ${c.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-slate-400">
                            <span className="uppercase font-mono">{c.plan}</span>
                            {' · '}
                            {new Date(c.created_at).toLocaleDateString('ja-JP')}
                          </div>
                        </div>
                        <button
                          onClick={() => toggleActive(c)}
                          className={`text-xs px-2 py-1 rounded ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}
                        >
                          {c.is_active ? '有効' : '無効'}
                        </button>
                      </div>
                      <div className="flex gap-3 text-xs text-slate-500">
                        <label className="flex items-center gap-1">
                          月上限
                          <input
                            type="number"
                            min="0"
                            value={editingLimits[`${c.id}_monthly_limit`] ?? c.monthly_limit ?? 0}
                            onChange={e => setEditingLimits(prev => ({ ...prev, [`${c.id}_monthly_limit`]: e.target.value }))}
                            onBlur={e => updateCompanyLimit(c.id, 'monthly_limit', e.target.value)}
                            className="w-20 px-1 py-0.5 border rounded text-xs"
                          />
                          件
                        </label>
                        <label className="flex items-center gap-1">
                          管理者上限
                          <input
                            type="number"
                            min="0"
                            value={editingLimits[`${c.id}_max_admin`] ?? c.max_admin ?? 0}
                            onChange={e => setEditingLimits(prev => ({ ...prev, [`${c.id}_max_admin`]: e.target.value }))}
                            onBlur={e => updateCompanyLimit(c.id, 'max_admin', e.target.value)}
                            className="w-12 px-1 py-0.5 border rounded text-xs"
                          />
                          名
                        </label>
                        <span className="text-slate-300 self-center">（0=無制限）</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                        <span>超過後も継続（後払い）</span>
                        <Toggle
                          value={!!c.allow_overage}
                          onChange={() => toggleAllowOverage(c)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'usage' && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 mb-3">今月の企業別照合回数</p>
                {usageSummary.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">データなし</p>
                ) : (
                  usageSummary.map(row => {
                    const company = companies.find(c => c.id === row.id);
                    const limit = company?.monthly_limit || 0;
                    const overage = limit > 0 ? Math.max(0, row.total - limit) : 0;
                    const pct = limit > 0 ? Math.min(100, Math.round((row.total / limit) * 100)) : null;
                    return (
                      <div key={row.id} className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm">{row.name}</span>
                          <div className="flex items-center gap-2">
                            {overage > 0 && (
                              <span className="text-xs font-bold px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                                超過 +{overage.toLocaleString()}回
                              </span>
                            )}
                            <span className="font-bold text-slate-700">{row.total.toLocaleString()} 回</span>
                          </div>
                        </div>
                        {pct !== null && (
                          <div>
                            <div className="flex justify-between text-xs mb-0.5">
                              <span className={pct >= 100 ? 'text-orange-600 font-bold' : pct >= 90 ? 'text-red-500 font-bold' : pct >= 70 ? 'text-orange-500' : 'text-slate-400'}>
                                {pct}%
                              </span>
                              <span className="text-slate-300">上限 {limit.toLocaleString()}件</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-orange-500' : pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : 'bg-indigo-400'}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {tab === 'emails' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-slate-500">メール登録ユーザー（{emailRegs.length}件）</p>
                  <button onClick={fetchEmailRegs} className="text-xs text-slate-400 hover:text-slate-600">更新</button>
                </div>
                {emailRegs.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">登録なし</p>
                ) : (
                  emailRegs.map(row => (
                    <div key={row.email} className="p-3 bg-white rounded-lg border">
                      <div className="font-medium text-sm break-all">{row.email}</div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-xs text-slate-400">
                          初回: {new Date(row.first_seen).toLocaleDateString('ja-JP')}
                          {' · '}
                          最終: {new Date(row.last_seen).toLocaleDateString('ja-JP')}
                        </div>
                        <span className="text-xs font-bold text-indigo-700">{row.match_count.toLocaleString()} 回</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            {tab === 'inspections' && (
              <div className="space-y-3">
                {/* 検索フォーム */}
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-slate-500">検査履歴を検索（最大100件）</p>
                  <input
                    type="number"
                    placeholder="シリアル番号（例: 1234）"
                    value={searchSerial}
                    onChange={(e) => setSearchSerial(e.target.value)}
                    className="w-full p-2 border rounded text-sm"
                  />
                  <select value={searchCompanyId} onChange={(e) => setSearchCompanyId(e.target.value)} className="w-full p-2 border rounded text-sm">
                    <option value="">企業（全件）</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)} className="w-full p-2 border rounded text-sm">
                    <option value="">判定（全件）</option>
                    <option value="SAFE">SAFE</option>
                    <option value="DANGER">DANGER</option>
                    <option value="ERROR">ERROR</option>
                  </select>
                  <div className="flex gap-2 items-center">
                    <input type="date" value={searchDateFrom} onChange={(e) => setSearchDateFrom(e.target.value)} className="flex-1 p-2 border rounded text-sm" />
                    <span className="text-slate-400 text-xs flex-shrink-0">〜</span>
                    <input type="date" value={searchDateTo} onChange={(e) => setSearchDateTo(e.target.value)} className="flex-1 p-2 border rounded text-sm" />
                  </div>
                  <button
                    onClick={fetchInspections}
                    disabled={searchLoading}
                    className="w-full py-2 bg-slate-700 text-white text-sm font-bold rounded disabled:bg-slate-300 flex items-center justify-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    {searchLoading ? '検索中...' : '検索'}
                  </button>
                </div>

                {/* 検索結果 */}
                {inspections.length > 0 && (
                  <button
                    onClick={exportInspectionsCSV}
                    className="w-full py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-bold rounded flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" /> CSV出力 ({inspections.length}件)
                  </button>
                )}
                {inspections.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {searchLoading ? '' : '条件を指定して検索してください'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400">{inspections.length}件</p>
                    {inspections.map(insp => {
                      const companyName = companies.find(c => c.id === insp.company_id)?.name || '—';
                      const isSelected = selectedInspection?.id === insp.id;
                      return (
                        <div key={insp.id} className="bg-white rounded-lg border overflow-hidden">
                          <button className="w-full text-left p-3" onClick={() => setSelectedInspection(isSelected ? null : insp)}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                {insp.serial != null
                                  ? <span className="font-mono text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">#{insp.serial}</span>
                                  : <span className="font-mono text-xs text-slate-300">—</span>
                                }
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  insp.match_status === 'DANGER' ? 'bg-red-100 text-red-700' :
                                  insp.match_status === 'ERROR' ? 'bg-slate-100 text-slate-500' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {insp.match_status === 'DANGER' ? <AlertTriangle className="w-3 h-3 inline mr-0.5" /> : insp.match_status === 'SAFE' ? <CheckCircle className="w-3 h-3 inline mr-0.5" /> : null}
                                  {insp.match_status}
                                </span>
                              </div>
                              <span className="text-xs text-slate-400">
                                {new Date(insp.inspected_at).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="text-xs text-slate-600">{companyName} · {insp.store_name || '—'} · {insp.dept_name || '—'}</div>
                            <div className="text-xs text-slate-500 truncate">{insp.label_product_name || '—'}</div>
                          </button>
                          {isSelected && (
                            <div className="border-t p-3 bg-slate-50 space-y-2">
                              {insp.image_urls && insp.image_urls.length > 0 && (
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                  {insp.image_urls.map((path, i) => (
                                    <StorageImage
                                      key={i}
                                      path={path}
                                      className="h-24 w-24 object-cover rounded border flex-shrink-0"
                                      alt={`img-${i}`}
                                    />
                                  ))}
                                </div>
                              )}
                              <div className="text-xs space-y-1 text-slate-600">
                                <div><span className="text-slate-400">メール:</span> {insp.user_email || '—'}</div>
                                <div><span className="text-slate-400">ロット:</span> {insp.lot_number || '—'}</div>
                                <div><span className="text-slate-400">目視:</span> {insp.detected_food_visual || '—'}</div>
                                {insp.reason && <div><span className="text-slate-400">判定理由:</span> {insp.reason}</div>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'settings' && (
              <div className="space-y-3">
                {/* ログイン設定 */}
                <div className="bg-white rounded-lg border divide-y">
                  <div className="px-4 py-3">
                    <p className="text-xs font-bold text-slate-500">ログイン設定</p>
                  </div>
                  <div className="flex items-center justify-between px-4 py-4 gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800">マジックリンク認証を必須にする</div>
                      <div className="text-xs text-slate-400 mt-0.5">ONにすると、メールのリンクをクリックしないとログインできなくなります</div>
                    </div>
                    <Toggle
                      value={!!settings.magic_link_required}
                      onChange={() => updateSetting('magic_link_required', !settings.magic_link_required)}
                      disabled={savingKey === 'magic_link_required'}
                    />
                  </div>
                </div>

                {/* 企業管理設定 */}
                <div className="bg-white rounded-lg border divide-y">
                  <div className="px-4 py-3">
                    <p className="text-xs font-bold text-slate-500">企業管理</p>
                  </div>
                  <div className="flex items-center justify-between px-4 py-4 gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800">企業作成・招待機能を有効にする</div>
                      <div className="text-xs text-slate-400 mt-0.5">ONにすると、契約企業タブから新規企業の作成と管理者招待ができます</div>
                    </div>
                    <Toggle
                      value={!!settings.invite_enabled}
                      onChange={() => updateSetting('invite_enabled', !settings.invite_enabled)}
                      disabled={savingKey === 'invite_enabled'}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export { CompanyAdminPanel, SuperAdminPanel };
